const React = require('react');
const { createElement: h, useMemo, useState, useEffect } = React;
const SchedulerPage = require('./SchedulerPage');
const WorkflowTagModal = require('./WorkflowTagModal');
const ApiKeysPage = require('./ApiKeysPage');
const ApiDocsPage = require('./ApiDocsPage');
const DeveloperAppsPage = require('./DeveloperAppsPage');
const UsagePage = require('./UsagePage');
const ConnectedAccountsPage = require('./ConnectedAccountsPage');
const AccountSettingsPage = require('./AccountSettingsPage');
const NodeIcon = require('./NodeIcon');
const { loadWorkflowStateById, saveWorkflowStateById } = require('../lib/workflowStorage');
const PublishTemplateModal = require('./PublishTemplateModal');
const SupportSessionsSection = require('./SupportSessionsSection');
require('./WorkflowListPage.css');

var DEFAULT_TAG_COLOR = '#64748b';

function formatDate(value) {
  if (!value) return '-';
  var date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
}

function formatTime(value) {
  if (!value) return '';
  var date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  var hours = date.getHours();
  var minutes = date.getMinutes();
  var ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return hours + ':' + (minutes < 10 ? '0' : '') + minutes + ' ' + ampm;
}

function normalizeTag(tag) {
  if (!tag) return null;
  if (typeof tag === 'string') {
    var nameFromString = String(tag || '').trim();
    if (!nameFromString) return null;
    return { name: nameFromString, color: DEFAULT_TAG_COLOR };
  }

  if (typeof tag === 'object') {
    var name = String(tag.name !== undefined && tag.name !== null ? tag.name : tag.label || '').trim();
    if (!name) return null;
    return {
      name: name,
      color: String(tag.color || DEFAULT_TAG_COLOR).trim() || DEFAULT_TAG_COLOR
    };
  }

  return null;
}

function normalizeTagList(tags) {
  var seen = {};
  return (Array.isArray(tags) ? tags : [])
    .map(normalizeTag)
    .filter(Boolean)
    .filter(function(tag) {
      var key = tag.name.toLowerCase();
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
}

function collectTagMap(workflows) {
  var map = {};
  (Array.isArray(workflows) ? workflows : []).forEach(function(workflow) {
    normalizeTagList(workflow && workflow.tags).forEach(function(tag) {
      var key = tag.name.toLowerCase();
      if (!map[key]) {
        map[key] = { name: tag.name, color: tag.color || DEFAULT_TAG_COLOR };
      }
    });
  });

  return Object.keys(map).sort(function(a, b) {
    return map[a].name.localeCompare(map[b].name);
  }).map(function(key) {
    return map[key];
  });
}

function workflowHasTag(workflow, activeTag) {
  if (!activeTag) return true;
  var tags = normalizeTagList(workflow && workflow.tags);
  var active = String(activeTag || '').trim().toLowerCase();
  return tags.some(function(tag) {
    return tag.name.toLowerCase() === active;
  });
}

function WorkflowCardIcon() {
  return h('svg', { className: 'wlp-card-icon', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z' }),
    h('path', { d: 'M13 2v7h7' })
  );
}

// Three-dot "more" glyph shown in the top-right of every workflow card. Clicking
// it opens the tag editor (kept from the prior "Tags" button behavior).
function WorkflowCardMenuIcon() {
  return h('svg', { className: 'wlp-card__menu-icon', viewBox: '0 0 24 24', fill: 'currentColor' },
    h('circle', { cx: 5, cy: 12, r: 1.6 }),
    h('circle', { cx: 12, cy: 12, r: 1.6 }),
    h('circle', { cx: 19, cy: 12, r: 1.6 })
  );
}

// Pick step icons that should appear on the workflow card.
// Show: (a) any node with an uploaded logoUrl (custom or agent), (b) every
// agent node even without a logoUrl — agents always get their robot glyph so
// users can tell at a glance the workflow has AI in it. Other built-in nodes
// (start, condition, classifier, set-variable, etc.) and custom nodes without
// an uploaded logo are skipped.
function pickCustomLogoIcons(stepIcons) {
  if (!Array.isArray(stepIcons)) return [];
  var seen = {};
  var result = [];
  for (var i = 0; i < stepIcons.length; i += 1) {
    var entry = stepIcons[i];
    if (!entry) continue;
    var logoUrl = String(entry.logoUrl || '').trim();
    var icon = String(entry.icon || '');
    var isAgent = icon === 'agent';
    if (!logoUrl && !isAgent) continue;
    var key = logoUrl || ('agent::' + icon);
    if (seen[key]) continue;
    seen[key] = true;
    result.push({ logoUrl: logoUrl, icon: icon, isAgent: isAgent });
    if (result.length >= 6) break;
  }
  return result;
}

function WorkflowListPage(props) {
  var workflows = Array.isArray(props && props.workflows) ? props.workflows : [];
  var workbooks = Array.isArray(props && props.workbooks) ? props.workbooks : [];
  var loading = !!(props && props.loading);
  var creating = !!(props && props.creating);
  var creatingWorkbook = !!(props && props.creatingWorkbook);
  var onCreateWorkflow = props && props.onCreateWorkflow ? props.onCreateWorkflow : function() {};
  var onCreateWorkbook = props && props.onCreateWorkbook ? props.onCreateWorkbook : function() {};
  var onOpenWorkbook = props && props.onOpenWorkbook ? props.onOpenWorkbook : function() {};
  var onDuplicateWorkflow = props && props.onDuplicateWorkflow ? props.onDuplicateWorkflow : function() {};
  var onImportWorkflowFiles = props && props.onImportWorkflowFiles ? props.onImportWorkflowFiles : null;
  var onOpenWorkflow = props && props.onOpenWorkflow ? props.onOpenWorkflow : function() {};
  var onSignout = props && props.onSignout ? props.onSignout : function() {};
  var onOpenStore = props && props.onOpenStore ? props.onOpenStore : function() {};
  var onRefreshWorkflows = props && props.onRefreshWorkflows ? props.onRefreshWorkflows : function() {};
  var user = props && props.user ? props.user : null;
  var userName = user && (user.username || user.name) ? String(user.username || user.name) : '';
  var initialTab = props && props.initialTab ? String(props.initialTab) : '';
  var validTabs = { workflows: 1, scheduler: 1, apikeys: 1, developer: 1, apidocs: 1, usage: 1, accounts: 1, settings: 1 };
  var [activePage, setActivePage] = useState(validTabs[initialTab] ? initialTab : 'workflows');
  var [navOpen, setNavOpen] = useState(false);

  // Single entry point used by every nav item — switches the page and closes
  // the mobile drawer so the user lands on the new view without an open menu
  // overlaying it. Desktop ignores navOpen entirely (no overlay there).
  function goToPage(page) {
    setActivePage(page);
    setNavOpen(false);
  }

  useEffect(function() {
    if (initialTab && validTabs[initialTab]) {
      setActivePage(initialTab);
    }
  }, [initialTab]);
  var [activeTag, setActiveTag] = useState('');
  var [searchTerm, setSearchTerm] = useState('');
  var [tagModalWorkflow, setTagModalWorkflow] = useState(null);
  var [isTagSaving, setIsTagSaving] = useState(false);
  var [tagModalError, setTagModalError] = useState('');
  var [publishWorkflow, setPublishWorkflow] = useState(null);
  var [importing, setImporting] = useState(false);
  var [importError, setImportError] = useState('');

  // Opens a file picker (multi-select) and hands the chosen .json files to the
  // app, which creates a new workflow per file. A single import opens its
  // canvas; multiple imports land as new cards in the list below.
  function handleImportClick() {
    if (!onImportWorkflowFiles || importing) return;
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.multiple = true;
    input.onchange = function(event) {
      var files = event && event.target && event.target.files ? event.target.files : null;
      if (!files || !files.length) return;
      setImporting(true);
      setImportError('');
      Promise.resolve(onImportWorkflowFiles(files))
        .catch(function(error) {
          setImportError(error && error.message ? error.message : 'Could not import workflows.');
        })
        .finally(function() {
          setImporting(false);
        });
    };
    input.click();
  }

  var availableTags = useMemo(function() {
    return collectTagMap(workflows);
  }, [workflows]);

  var visibleWorkflows = useMemo(function() {
    var query = String(searchTerm || '').trim().toLowerCase();
    // Hide workflows that already live inside a workbook — those are reached
    // by opening their workbook. Otherwise the standalone card would just
    // redirect to the workbook URL, which looks like a bug to the user.
    return (Array.isArray(workflows) ? workflows : []).filter(function(workflow) {
      if (workflow && workflow.workbookId) return false;
      if (!workflowHasTag(workflow, activeTag)) return false;
      if (query) {
        var name = String(workflow && workflow.workflowName || 'Untitled').toLowerCase();
        if (name.indexOf(query) === -1) return false;
      }
      return true;
    });
  }, [workflows, activeTag, searchTerm]);

  // Browser workflows (contain a Browser Action / Browser Run step) run
  // inside the user's Chrome — they can't run in the background or be
  // scheduled, so they live in their own section under the regular grid.
  var regularWorkflows = useMemo(function() {
    return visibleWorkflows.filter(function(w) { return !(w && w.hasBrowserNode); });
  }, [visibleWorkflows]);
  var browserWorkflows = useMemo(function() {
    return visibleWorkflows.filter(function(w) { return !!(w && w.hasBrowserNode); });
  }, [visibleWorkflows]);

  function handleSubmit(event) {
    event.preventDefault();
    onCreateWorkflow('');
  }

  function openTagEditor(workflow) {
    if (!workflow || !workflow.id) return;
    var workflowId = String(workflow.id);
    var fallbackTags = normalizeTagList(workflow.tags);
    setTagModalError('');
    setTagModalWorkflow({
      id: workflowId,
      workflowName: workflow.workflowName || 'Untitled Workflow',
      tags: fallbackTags
    });

    loadWorkflowStateById(workflowId)
      .then(function(state) {
        if (!state || !Array.isArray(state.nodes)) return null;
        var nextTags = normalizeTagList(state.tags);
        setTagModalWorkflow(function(prev) {
          if (!prev || prev.id !== workflowId) return prev;
          return Object.assign({}, prev, { tags: nextTags });
        });
        return null;
      })
      .catch(function() {
        // Keep the fallback tags from the summary.
      });
  }

  function closeTagEditor() {
    if (isTagSaving) return;
    setTagModalWorkflow(null);
    setTagModalError('');
  }

  function saveTagEditor(nextTags) {
    if (!tagModalWorkflow || !tagModalWorkflow.id) return;
    var workflowId = String(tagModalWorkflow.id);
    var workflowName = String(tagModalWorkflow.workflowName || 'Untitled Workflow');
    var tags = normalizeTagList(nextTags);

    setIsTagSaving(true);
    setTagModalError('');

    loadWorkflowStateById(workflowId)
      .then(function(state) {
        if (!state || !Array.isArray(state.nodes)) {
          throw new Error('Could not load workflow to save tags.');
        }
        var nextState = Object.assign({}, state, {
          workflowName: state.workflowName || workflowName,
          tags: tags
        });
        return saveWorkflowStateById(workflowId, nextState);
      })
      .then(function() {
        setTagModalWorkflow(null);
        onRefreshWorkflows();
      })
      .catch(function(error) {
        setTagModalError(error && error.message ? error.message : 'Could not save tags.');
      })
      .finally(function() {
        setIsTagSaving(false);
      });
  }

  function renderTagPill(tag, extraClassName, onClick) {
    if (!tag) return null;
    return h('button', {
      key: tag.name,
      type: 'button',
      className: 'wlp-tag-pill' + (extraClassName ? ' ' + extraClassName : ''),
      style: { '--tag-color': tag.color || DEFAULT_TAG_COLOR },
      onClick: onClick || null
    },
      h('span', { className: 'wlp-tag-pill__dot' }),
      h('span', { className: 'wlp-tag-pill__text' }, tag.name)
    );
  }

  // One workflow card — shared by the regular grid and the "Browser
  // workflows" section below it.
  function renderWorkflowCard(item) {
    var id = String(item && item.id || '');
    var name = item && item.workflowName ? String(item.workflowName) : 'Untitled';
    var tags = normalizeTagList(item && item.tags);
    var logoIcons = pickCustomLogoIcons(item && item.stepIcons);
    return h('article', {
      key: id,
      className: 'wlp-card',
      onClick: function() { onOpenWorkflow(id); }
    },
      h('div', { className: 'wlp-card__top' },
        h('div', { className: 'wlp-card__top-left' },
          logoIcons.length
            ? h('div', { className: 'wlp-card__logos' },
                logoIcons.map(function(li, idx) {
                  return h('div', {
                    key: (li.logoUrl || li.icon) + '-' + idx,
                    className: 'wlp-card__logo' + (li.isAgent && !li.logoUrl ? ' wlp-card__logo--agent' : '')
                  },
                    li.logoUrl
                      ? h('img', {
                          src: li.logoUrl,
                          alt: '',
                          className: 'wlp-card__logo-img',
                          onError: function(e) { e.target.style.display = 'none'; }
                        })
                      : h(NodeIcon, { type: li.icon, size: 18 })
                  );
                })
              )
            : item && item.hasBrowserNode
              ? h('div', { className: 'wlp-card__icon-wrap wlp-card__icon-wrap--browser' },
                  h(NodeIcon, { type: 'browser-action', size: 18 })
                )
              : h('div', { className: 'wlp-card__icon-wrap' }, h(WorkflowCardIcon))
        ),
        h('div', { className: 'wlp-card__top-right' },
          item && item.isLive
            ? h('span', {
                className: 'wlp-card__live',
                title: 'This workflow has a published live version'
              },
                h('span', { className: 'wlp-card__live-dot' }),
                'Live'
              )
            : null,
          h('button', {
            type: 'button',
            className: 'wlp-card__menu',
            'aria-label': 'Edit tags',
            onClick: function(e) {
              e.stopPropagation();
              openTagEditor(item);
            }
          }, h(WorkflowCardMenuIcon))
        )
      ),
      h('h2', {
        className: 'wlp-card__title',
        title: name
      }, name),
      tags.length
        ? h('div', { className: 'wlp-card__tags' },
            tags.map(function(tag) {
              return renderTagPill(tag, '', function(e) {
                e.stopPropagation();
                setActiveTag(tag.name);
              });
            })
          )
        : h('button', {
            type: 'button',
            className: 'wlp-card__add-tags',
            onClick: function(e) {
              e.stopPropagation();
              openTagEditor(item);
            }
          }, '+ Add tags'),
      h('div', { className: 'wlp-card__meta' },
        h('div', null,
          h('span', { className: 'wlp-card__meta-label' }, 'Created'),
          h('span', { className: 'wlp-card__meta-value' }, formatDate(item && item.createdAt))
        ),
        h('div', null,
          h('span', { className: 'wlp-card__meta-label' }, 'Updated'),
          h('span', { className: 'wlp-card__meta-value' }, formatDate(item && item.updatedAt) + (formatTime(item && item.updatedAt) ? ' · ' + formatTime(item && item.updatedAt) : ''))
        )
      )
    );
  }

  return h('div', { className: 'wlp-root' + (navOpen ? ' wlp-nav-open' : '') },
    // Mobile-only hamburger that slides the sidebar in from the left. The
    // sidebar is display:none on phones by default — without this trigger the
    // user has no way to reach Scheduler/API/Accounts/Settings/Sign out.
    h('button', {
      type: 'button',
      className: 'wlp-mobile-nav-btn',
      'aria-label': 'Open navigation',
      'aria-expanded': navOpen ? 'true' : 'false',
      onClick: function() { setNavOpen(true); }
    },
      h('svg', { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('line', { x1: 3, y1: 6, x2: 21, y2: 6 }),
        h('line', { x1: 3, y1: 12, x2: 21, y2: 12 }),
        h('line', { x1: 3, y1: 18, x2: 21, y2: 18 })
      )
    ),
    // Backdrop sits behind the drawer; tap closes. Only rendered when open so
    // it can't intercept clicks on desktop.
    navOpen ? h('div', {
      className: 'wlp-mobile-nav-backdrop',
      onClick: function() { setNavOpen(false); }
    }) : null,
    h('aside', { className: 'wlp-sidebar' },
      h('div', { className: 'wlp-sidebar-logo' },
        h('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('circle', { cx: 12, cy: 12, r: 10 }),
          h('path', { d: 'M8 12l2 2 4-4' })
        ),
        h('span', { className: 'wlp-sidebar-brand' }, 'Alleflow')
      ),
      h('nav', { className: 'wlp-sidebar-nav' },
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'workflows' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('workflows'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
            h('rect', { x: 3, y: 3, width: 7, height: 7, rx: 1 }),
            h('rect', { x: 14, y: 3, width: 7, height: 7, rx: 1 }),
            h('rect', { x: 3, y: 14, width: 7, height: 7, rx: 1 }),
            h('rect', { x: 14, y: 14, width: 7, height: 7, rx: 1 })
          ),
          h('span', null, 'Workflows')
        ),
        h('div', {
          className: 'wlp-sidebar-item',
          onClick: function() { onOpenStore(); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M3 9l1.5-5h15L21 9' }),
            h('path', { d: 'M4 9h16v10a1 1 0 01-1 1H5a1 1 0 01-1-1z' }),
            h('path', { d: 'M9 13h6' })
          ),
          h('span', null, 'Template Store')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'scheduler' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('scheduler'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
            h('circle', { cx: 12, cy: 12, r: 9 }),
            h('path', { d: 'M12 7v5l3 3' })
          ),
          h('span', null, 'Scheduler')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'apikeys' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('apikeys'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4' })
          ),
          h('span', null, 'API Keys')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'developer' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('developer'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('polyline', { points: '16 18 22 12 16 6' }),
            h('polyline', { points: '8 6 2 12 8 18' })
          ),
          h('span', null, 'Developer')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'apidocs' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('apidocs'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z' }),
            h('path', { d: 'M14 2v6h6' }),
            h('path', { d: 'M16 13H8' }),
            h('path', { d: 'M16 17H8' }),
            h('path', { d: 'M10 9H8' })
          ),
          h('span', null, 'API Docs')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'usage' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('usage'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M18 20V10' }),
            h('path', { d: 'M12 20V4' }),
            h('path', { d: 'M6 20v-6' })
          ),
          h('span', null, 'Usage')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'accounts' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('accounts'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M10 13a5 5 0 007.07 0l2.83-2.83a5 5 0 00-7.07-7.07L11 4' }),
            h('path', { d: 'M14 11a5 5 0 00-7.07 0L4.1 13.83a5 5 0 107.07 7.07L13 20' })
          ),
          h('span', null, 'Accounts')
        ),
        h('div', {
          className: 'wlp-sidebar-item' + (activePage === 'settings' ? ' wlp-sidebar-item-active' : ''),
          onClick: function() { goToPage('settings'); }
        },
          h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('circle', { cx: 12, cy: 12, r: 3 }),
            h('path', { d: 'M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z' })
          ),
          h('span', null, 'Settings')
        )
      ),
      h('div', { className: 'wlp-sidebar-footer' },
        h('div', { className: 'wlp-sidebar-user' },
          h('div', { className: 'wlp-sidebar-avatar' }, userName.charAt(0).toUpperCase()),
          h('span', { className: 'wlp-sidebar-username' }, userName)
        ),
        h('button', { className: 'wlp-sidebar-signout', onClick: onSignout }, 'Sign out')
      )
    ),
    activePage === 'scheduler'
      ? h(SchedulerPage, { workflows: workflows })
      : null,
    activePage === 'apikeys'
      ? h(ApiKeysPage)
      : null,
    activePage === 'developer'
      ? h(DeveloperAppsPage)
      : null,
    activePage === 'apidocs'
      ? h(ApiDocsPage)
      : null,
    activePage === 'usage'
      ? h(UsagePage, { user: user, workflows: workflows, onResetUsage: props && props.onResetUsage ? props.onResetUsage : function() {} })
      : null,
    activePage === 'accounts'
      ? h(ConnectedAccountsPage, { user: user, onRefreshUser: props && props.onRefreshUser ? props.onRefreshUser : null })
      : null,
    activePage === 'settings'
      ? h(AccountSettingsPage, {
          user: user,
          language: props && props.language,
          onChangeLanguage: props && props.onChangeLanguage
        })
      : null,
    activePage === 'workflows'
      ? h('div', { className: 'wlp-content' },
          h('header', { className: 'wlp-hero' },
            h('p', { className: 'wlp-hero__eyebrow' }, 'Workflow library'),
            h('h1', { className: 'wlp-hero__title' }, 'Create a workflow'),
            h('p', { className: 'wlp-hero__copy' }, 'Build a workflow, tag it by purpose, and filter the library by the tags that matter most.'),
            h('form', { className: 'wlp-create-form', onSubmit: handleSubmit },
              h('button', {
                className: 'wlp-create-btn',
                type: 'submit',
                disabled: creating
              },
                h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
                  h('path', { d: 'M12 5v14M5 12h14' })
                ),
                creating ? 'Creating...' : 'Create workflow'
              ),
              h('button', {
                className: 'wlp-create-btn wlp-create-btn--secondary',
                type: 'button',
                disabled: creatingWorkbook,
                onClick: function() { onCreateWorkbook(); }
              },
                h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                  h('path', { d: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z' }),
                  h('path', { d: 'M12 11v6M9 14h6' })
                ),
                creatingWorkbook ? 'Creating...' : 'Create playbook'
              ),
              onImportWorkflowFiles
                ? h('button', {
                    className: 'wlp-create-btn wlp-create-btn--secondary',
                    type: 'button',
                    disabled: importing,
                    onClick: handleImportClick
                  },
                    h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                      h('path', { d: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4' }),
                      h('path', { d: 'M17 8l-5-5-5 5' }),
                      h('path', { d: 'M12 3v12' })
                    ),
                    importing ? 'Importing...' : 'Import workflow'
                  )
                : null
            ),
            importError
              ? h('p', { className: 'wlp-import-error' }, importError)
              : null
          ),
          workbooks.length > 0
            ? h('section', { className: 'wlp-workbooks' },
                h('h2', { className: 'wlp-section-title' }, 'Playbooks'),
                h('div', { className: 'wlp-workbook-grid' },
                  workbooks.map(function(wb) {
                    var id = String(wb && wb.id || '');
                    var name = wb && wb.name ? String(wb.name) : 'Untitled playbook';
                    return h('article', {
                      key: id,
                      className: 'wlp-workbook-card',
                      onClick: function() { onOpenWorkbook(id); }
                    },
                      h('div', { className: 'wlp-workbook-card__icon' },
                        h('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' },
                          h('path', { d: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z' })
                        )
                      ),
                      h('div', { className: 'wlp-workbook-card__body' },
                        h('h3', { className: 'wlp-workbook-card__title', title: name }, name),
                        h('p', { className: 'wlp-workbook-card__meta' },
                          (wb && wb.workflowCount ? wb.workflowCount : 0) + ' flow' + ((wb && wb.workflowCount === 1) ? '' : 's')
                          + ' · ' +
                          (wb && wb.folderCount ? wb.folderCount : 0) + ' folder' + ((wb && wb.folderCount === 1) ? '' : 's')
                        )
                      )
                    );
                  })
                )
              )
            : null,
          // Remote Support: owners who added THIS account as a support agent
          // and are live right now. Renders nothing when there's no session.
          h(SupportSessionsSection, {
            onJoin: props.onJoinSupportSession,
            notice: props.supportSessionNotice,
            onDismissNotice: props.onDismissSupportSessionNotice
          }),
          h('div', { className: 'wlp-filterbar' },
            h('div', { className: 'wlp-filterbar__group' },
              h('button', {
                type: 'button',
                className: 'wlp-filter-chip' + (!activeTag ? ' wlp-filter-chip--active' : ''),
                onClick: function() { setActiveTag(''); }
              }, 'All'),
              availableTags.map(function(tag) {
                return renderTagPill(tag, activeTag && activeTag.toLowerCase() === tag.name.toLowerCase() ? 'wlp-tag-pill--active' : '', function() {
                  setActiveTag(tag.name);
                });
              })
            ),
            h('div', { className: 'wlp-filterbar__meta' },
              h('div', { className: 'wlp-search' },
                h('svg', { className: 'wlp-search__icon', width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
                  h('circle', { cx: 11, cy: 11, r: 7 }),
                  h('path', { d: 'M21 21l-4.3-4.3' })
                ),
                h('input', {
                  type: 'text',
                  className: 'wlp-search__input',
                  placeholder: 'Search workflows',
                  value: searchTerm,
                  onChange: function(e) { setSearchTerm(e.target.value); }
                })
              ),
              h('span', { className: 'wlp-filterbar__count' }, String(visibleWorkflows.length) + ' shown'),
              activeTag
                ? h('button', {
                    type: 'button',
                    className: 'wlp-clear-filter',
                    onClick: function() { setActiveTag(''); }
                  }, 'Clear filter')
                : null
            )
          ),
          loading
            ? h('div', { className: 'wlp-empty wlp-empty--loading' }, 'Loading workflows...')
            : null,
          !loading && workflows.length === 0
            ? h('div', { className: 'wlp-empty' }, 'No workflows yet. Create your first one.')
            : null,
          !loading && workflows.length > 0 && regularWorkflows.length > 0
            ? h('div', { className: 'wlp-grid' },
                regularWorkflows.map(renderWorkflowCard)
              )
            : null,
          !loading && workflows.length > 0 && browserWorkflows.length > 0
            ? h('section', { className: 'wlp-browser-section' },
                h('h2', { className: 'wlp-section-title' }, 'Browser workflows'),
                h('p', { className: 'wlp-browser-section__hint' },
                  'These run in your Chrome via the Browser Action extension — they can’t run in the background or be scheduled.'),
                h('div', { className: 'wlp-grid' },
                  browserWorkflows.map(renderWorkflowCard)
                )
              )
            : null,
          !loading && workflows.length > 0 && visibleWorkflows.length === 0
            ? h('div', { className: 'wlp-empty wlp-empty--filtered' },
                h('p', null, searchTerm ? 'No workflows match your search.' : 'No workflows match this tag.'),
                h('button', {
                  type: 'button',
                  className: 'wlp-clear-filter',
                  onClick: function() { setActiveTag(''); setSearchTerm(''); }
                }, 'Show all')
              )
            : null
        )
      : null,
    tagModalWorkflow
      ? h(WorkflowTagModal, {
          workflowName: tagModalWorkflow.workflowName,
          tags: tagModalWorkflow.tags,
          suggestedTags: availableTags,
          saving: isTagSaving,
          onSave: function(nextTags) {
            if (isTagSaving) return;
            saveTagEditor(nextTags);
          },
          onClose: closeTagEditor
        })
      : null,
    tagModalError
      ? h('div', { className: 'wlp-toast' }, tagModalError)
      : null,
    publishWorkflow
      ? h(PublishTemplateModal, {
          workflow: publishWorkflow,
          onClose: function() { setPublishWorkflow(null); },
          onPublished: function() { setPublishWorkflow(null); }
        })
      : null
  );
}

module.exports = WorkflowListPage;
